import { useState } from 'react'

const C = {
  bg:        '#05050d',
  surface:   'rgba(255,255,255,0.028)',
  border:    'rgba(180,185,230,0.09)',
  borderHi:  'rgba(180,185,230,0.2)',
  accent:    '#b8bdd4',
  accentDim: 'rgba(180,185,230,0.12)',
  text:      '#dce0f0',
  textMuted: '#8890aa',
  green:     '#4dc88a',
  yellow:    '#e0b84a',
  red:       '#d45a5a',
}
const font = { body: "'Inter', sans-serif", mono: "'Space Mono', monospace" }

function confidenceColor(v) {
  if (v >= 75) return C.green
  if (v >= 50) return C.yellow
  return C.red
}

function ConfidenceBadge({ value }) {
  if (!value && value !== 0) return null
  return (
    <span style={{
      display: 'inline-block', marginLeft: 8,
      padding: '1px 7px', borderRadius: 10, fontSize: 11,
      fontFamily: font.body, fontWeight: 600,
      background: `${confidenceColor(value)}22`,
      border: `1px solid ${confidenceColor(value)}66`,
      color: confidenceColor(value),
    }}>
      {value}%
    </span>
  )
}

function QuotesList({ quotes }) {
  const [showAll, setShowAll] = useState(false)
  const PAGE = 10
  const visible = showAll ? quotes : quotes.slice(0, PAGE)
  return (
    <div style={{ marginBottom: 8 }}>
      {visible.map((q, i) => {
        const text = typeof q === 'string' ? q : q?.text || q?.value || q?.pattern || ''
        if (!text) return null
        return (
        <div key={i} style={{
          padding: '4px 10px', marginBottom: 4,
          background: C.accentDim, borderLeft: `2px solid ${C.accent}`,
          fontSize: 12, color: C.text, fontStyle: 'italic', fontFamily: font.body,
        }}>
          «{text}»
        </div>
      )})}
      {!showAll && quotes.length > PAGE && (
        <button
          onClick={() => setShowAll(true)}
          style={{
            fontSize: 11, padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
            fontFamily: font.body, background: 'transparent',
            border: `1px solid ${C.border}`, color: C.textMuted, marginTop: 2,
          }}
        >
          Ещё {quotes.length - PAGE} цитат
        </button>
      )}
    </div>
  )
}

function EvidenceItem({ ev }) {
  const [open, setOpen] = useState(false)
  if (!ev) return null

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <ConfidenceBadge value={ev.confidence} />
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            marginLeft: 'auto', background: 'none', border: 'none',
            color: C.textMuted, cursor: 'pointer', fontSize: 11,
            fontFamily: font.body, padding: '0 4px',
          }}
        >
          {open ? '▲ скрыть' : '▼ подробнее'}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 8, paddingLeft: 4 }}>
          {ev.pattern && (
            <p style={{ margin: '0 0 8px', fontSize: 12, color: C.textMuted, lineHeight: 1.5, fontFamily: font.body }}>
              {typeof ev.pattern === 'string' ? ev.pattern : JSON.stringify(ev.pattern)}
            </p>
          )}
          {ev.quotes?.length > 0 && (
            <QuotesList quotes={ev.quotes} />
          )}
        </div>
      )}
    </div>
  )
}

// Обёртка для строкового поля с evidence
export function FieldWithEvidence({ label, value, ev, style: extraStyle }) {
  return (
    <div style={{ marginBottom: 12, ...extraStyle }}>
      <div>
        <strong style={{ color: C.accent }}>{label}:</strong>{' '}
        <span style={{ color: C.text }}>{value}</span>
      </div>
      <EvidenceItem ev={ev} />
    </div>
  )
}

// Обёртка для списка с evidence
export function ListWithEvidence({ label, items, evidenceItems }) {
  if (!items?.length) return null
  return (
    <div style={{ marginBottom: 12 }}>
      <strong style={{ color: C.accent }}>{label}:</strong>
      <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
        {items.map((item, i) => {
          const ev = evidenceItems?.[i]
          const text = typeof item === 'string' ? item : item?.value || item?.name || JSON.stringify(item)
          return (
            <li key={i} style={{ marginBottom: 6, color: C.text, fontSize: 14, fontFamily: font.body }}>
              {text}
              <EvidenceItem ev={ev} />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const ARCHETYPE_RU = {
  hero: 'Герой', sage: 'Мудрец', creator: 'Творец', ruler: 'Правитель',
  caregiver: 'Заботливый', explorer: 'Исследователь', rebel: 'Бунтарь',
  magician: 'Маг', lover: 'Влюблённый', jester: 'Шут', innocent: 'Невинный',
  orphan: 'Сирота', extra_e: 'Экстраверсия', extra_i: 'Интроверсия',
  extra_n: 'Интуиция', extra_g: 'Планирование',
  mbti_s: 'Сенсорика', mbti_n: 'Интуиция(N)', mbti_t: 'Логика',
  mbti_f: 'Этика', mbti_j: 'Планир.(J)', mbti_p: 'Восприятие',
}

const AGREE_COLOR   = { border: '#4dc88a40', bg: 'rgba(77,200,138,0.05)', dot: '#4dc88a' }
const NEUTRAL_COLOR = { border: 'rgba(180,185,230,0.12)', bg: 'rgba(255,255,255,0.02)', dot: '#8890aa' }
const DISAGREE_COLOR = { border: '#d45a5a40', bg: 'rgba(212,90,90,0.05)', dot: '#d45a5a' }

function responseColor(answerValue) {
  if (answerValue <= 2) return AGREE_COLOR
  return DISAGREE_COLOR
}

// Таб «Из анкеты» — список ответов сгруппированных по архетипу
export function SurveyEvidencePanel({ responses = [], dataEvidenceMap = null }) {
  const [expandedArchetype, setExpandedArchetype] = useState(null)

  if (!responses.length) return (
    <div style={{ padding: '40px 0', textAlign: 'center', color: C.textMuted, fontFamily: font.body, fontSize: 13 }}>
      Анкета ещё не пройдена
    </div>
  )

  // Группируем по архетипу
  const grouped = {}
  for (const r of responses) {
    const key = r.archetype || 'other'
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(r)
  }

  // Нормализованный score: avg(5 - answer_value) → процент [0..100]
  // answer_value: 1=полностью согласен(+4), 2=скорее(+3), 3=скорее нет(+2), 4=категорически нет(+1)
  const archetypeScore = (items) => {
    const sum = items.reduce((acc, r) => acc + (5 - r.answer_value), 0)
    const avg = sum / items.length  // 1..4
    return Math.round((avg - 1) / 3 * 100)
  }

  // MBTI: aggregate by dimension pair (E/I, S/N, T/F, J/P)
  const MBTI_KEYS = ['extra_e', 'extra_i', 'extra_n', 'extra_g',
                     'mbti_s', 'mbti_n', 'mbti_t', 'mbti_f', 'mbti_j', 'mbti_p']
  const MBTI_DIMS = [
    { label: 'E / I', a: { keys: ['extra_e'], name: 'Экстраверсия' }, b: { keys: ['extra_i'], name: 'Интроверсия' } },
    { label: 'S / N', a: { keys: ['mbti_s'], name: 'Сенсорика' }, b: { keys: ['extra_n', 'mbti_n'], name: 'Интуиция' } },
    { label: 'T / F', a: { keys: ['mbti_t'], name: 'Логика' }, b: { keys: ['mbti_f'], name: 'Этика' } },
    { label: 'J / P', a: { keys: ['extra_g', 'mbti_j'], name: 'Планирование' }, b: { keys: ['mbti_p'], name: 'Восприятие' } },
  ]

  const mainEntries = Object.entries(grouped)
    .filter(([k]) => !MBTI_KEYS.includes(k))
    .sort((a, b) => archetypeScore(b[1]) - archetypeScore(a[1]))

  const scoreColor = (pct) => {
    if (pct >= 75) return '#4dc88a'
    if (pct >= 50) return C.accent
    if (pct >= 30) return C.silverLo
    return '#d45a5a'
  }

  const ArchetypeRow = ({ archetype, items }) => {
    const label = ARCHETYPE_RU[archetype] || archetype
    const isOpen = expandedArchetype === archetype
    const pct = archetypeScore(items)
    const agreeCount = items.filter(r => r.answer_value <= 2).length
    const disagreeCount = items.filter(r => r.answer_value > 2).length
    return (
      <div style={{ marginBottom: 8, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <button
          onClick={() => setExpandedArchetype(isOpen ? null : archetype)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px', background: isOpen ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
            border: 'none', cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, minWidth: 90 }}>
            {label}
          </span>
          {/* Нормализованный балл */}
          <span style={{ fontFamily: font.mono, fontSize: 13, fontWeight: 600, color: scoreColor(pct), minWidth: 42 }}>
            {pct}%
          </span>
          {/* Прогресс-бар */}
          <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: scoreColor(pct), borderRadius: 2, transition: 'width 0.4s' }} />
          </div>
          <span style={{ fontSize: 11, color: C.textMuted, minWidth: 52, textAlign: 'right' }}>
            {items.length} вопр.
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            {agreeCount > 0 && <span style={{ fontSize: 10, color: '#4dc88a', fontFamily: font.mono }}>+{agreeCount}</span>}
            {disagreeCount > 0 && <span style={{ fontSize: 10, color: '#d45a5a', fontFamily: font.mono }}>−{disagreeCount}</span>}
          </div>
          <span style={{ fontSize: 11, color: C.textMuted }}>{isOpen ? '▲' : '▼'}</span>
        </button>
            {isOpen && (
              <div style={{ borderTop: `1px solid ${C.border}`, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map((r, i) => {
                  const col = responseColor(r.answer_value)
                  return (
                    <div key={i} style={{
                      background: col.bg, border: `1px solid ${col.border}`,
                      borderRadius: 8, padding: '10px 14px',
                    }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{
                          width: 6, height: 6, borderRadius: '50%', marginTop: 5,
                          background: col.dot, marginTop: 6, flexShrink: 0,
                        }} />
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 6px', fontSize: 13, color: C.text, lineHeight: 1.45 }}>
                            {r.question_text}
                          </p>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{
                              fontFamily: font.mono, fontSize: 10, color: col.dot,
                              background: `${col.dot}15`, border: `1px solid ${col.dot}40`,
                              borderRadius: 4, padding: '2px 8px',
                            }}>
                              {r.answer_label}
                            </span>
                            {r.group_num === 2 && r.is_check && (
                              <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted }}>
                                проверочный
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
  }

  return (
    <div style={{ fontFamily: font.body, color: C.text }}>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 20px', lineHeight: 1.55 }}>
        Ответы из анкеты по архетипам Юнга-Пирсон. Нормализованный балл = среднее по шкале / количество вопросов.
        {dataEvidenceMap && ' Рядом с каждым архетипом — статус совпадения с загруженными данными.'}
      </p>
      {mainEntries.map(([archetype, items]) => (
        <ArchetypeRow key={archetype} archetype={archetype} items={items} />
      ))}
      {MBTI_DIMS.some(d => [...d.a.keys, ...d.b.keys].some(k => grouped[k])) && (
        <>
          <div style={{ fontSize: 11, fontFamily: font.mono, color: C.textMuted, margin: '20px 0 8px', letterSpacing: '0.05em' }}>
            MBTI-шкалы
          </div>
          {MBTI_DIMS.map(dim => {
            const itemsA = dim.a.keys.flatMap(k => grouped[k] || [])
            const itemsB = dim.b.keys.flatMap(k => grouped[k] || [])
            const allItems = [...itemsA, ...itemsB]
            if (!allItems.length) return null
            const pctA = itemsA.length ? archetypeScore(itemsA) : null
            const pctB = itemsB.length ? archetypeScore(itemsB) : null
            const winner = pctA !== null && pctB !== null
              ? (pctA >= pctB ? dim.a.name : dim.b.name)
              : pctA !== null ? dim.a.name : dim.b.name
            const winnerPct = pctA !== null && pctB !== null
              ? Math.max(pctA, pctB)
              : pctA ?? pctB
            return (
              <div key={dim.label} style={{
                marginBottom: 8, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden',
                padding: '12px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: pctA !== null && pctB !== null ? 8 : 0 }}>
                  <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, minWidth: 40 }}>{dim.label}</span>
                  <span style={{ fontFamily: font.mono, fontSize: 12, fontWeight: 600, color: scoreColor(winnerPct) }}>
                    {winner}
                  </span>
                  <span style={{ fontFamily: font.mono, fontSize: 12, color: scoreColor(winnerPct) }}>{winnerPct}%</span>
                  <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 'auto' }}>{allItems.length} вопр.</span>
                </div>
                {pctA !== null && pctB !== null && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: C.textMuted, minWidth: 90 }}>{dim.a.name} {pctA}%</span>
                    <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2, overflow: 'hidden', display: 'flex' }}>
                      <div style={{ height: '100%', width: `${pctA}%`, background: pctA >= pctB ? scoreColor(pctA) : C.border, borderRadius: 2 }} />
                    </div>
                    <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'row-reverse' }}>
                      <div style={{ height: '100%', width: `${pctB}%`, background: pctB > pctA ? scoreColor(pctB) : C.border, borderRadius: 2 }} />
                    </div>
                    <span style={{ fontSize: 11, color: C.textMuted, minWidth: 90, textAlign: 'right' }}>{pctB}% {dim.b.name}</span>
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
